import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import classNames from 'classnames';
import styled from 'styled-components';
import { observer } from 'mobx-react-lite';

import {
    noticeError,
    IconChooser,
    Sizes,
    Colours,
} from '@ratehub/base-ui';

import PRODUCT_TYPES from '../definitions/ProductTypes';
import FOCUS_VARIANTS from '../definitions/FocusVariants';
import CreditCardShape from '../definitions/CreditCardShape';
import CardLayoutFocusText from './CardLayoutFocusText';
import CardLayoutFocusDetails from './CardLayoutFocusDetails';


function CardLayoutHighlights({
    cardInfo,
    productType,
    focusVariant,
    className,
    isCIBC = false,
    isEqualizeHeight = false,
}) {
    const isCIBC_BT = isCIBC && PRODUCT_TYPES.BALANCE_TRANSFER === productType;

    return (
        <section className={classNames(className,
            `rh-display-flex rh-flex-wrap rh-border-radius-8px rh-box-sizing-border-box
            rh-border-style-solid rh-border-width-1px rh-border-color-yuzu-dark`)}
        >
            <If
                // do not show <CardLayoutFocusText /> for LI or BT for CIBC since they include rates or annual fee
                condition={
                    !(isCIBC
                    && [ PRODUCT_TYPES.LOW_INTEREST, PRODUCT_TYPES.BALANCE_TRANSFER ].includes(productType))
                }
            >
                <div className={`focus-area rh-px-1 rh-pt-1 rh-pb-0_5
                    rh-border-radius-8px rh-position-relative rh-bg-yuzu-lightest rh-box-sizing-border-box`}
                >
                    <FocusCrown
                        productType={productType}
                        focusVariant={focusVariant}
                    />

                    <CardLayoutFocusText
                        cardInfo={cardInfo}
                        productType={productType || cardInfo.productType}
                        focusVariant={focusVariant}
                        isCIBC={isCIBC}
                    />
                </div>
            </If>

            <div className="details-area rh-box-sizing-border-box rh-position-relative">
                {/* - for CIBC/BT, we’ll be adding the FocusCrown on top of <CardLayoutFocusDetails />
                      not on top of the typical <CardLayoutFocusText />
                    - for LI, we leave that to the internal logic of <FocusCrown />
                */}
                <If condition={isCIBC_BT}>
                    <FocusCrown
                        productType={productType}
                        focusVariant={focusVariant}
                    />
                </If>

                <CardLayoutFocusDetails
                    cardInfo={cardInfo}
                    productType={productType || cardInfo.productType}
                    isCIBC={isCIBC}
                    className={classNames({ 'rh-pt-1': isCIBC_BT })}
                    isEqualizeHeight={isEqualizeHeight}
                />
            </div>
        </section>
    );
}

CardLayoutHighlights.propTypes = {
    cardInfo: PropTypes.shape(CreditCardShape).isRequired,
    productType: PropTypes.oneOf(Object.values(PRODUCT_TYPES)),
    focusVariant: PropTypes.oneOf(Object.values(FOCUS_VARIANTS)),
    className: PropTypes.string,
    isCIBC: PropTypes.bool,
    isEqualizeHeight: PropTypes.bool,
};


function FocusCrown({ productType, focusVariant }) {
    let iconKey,
        message;

    switch (productType) {
        case PRODUCT_TYPES.TRAVEL:
        case PRODUCT_TYPES.CASH_BACK:
        case PRODUCT_TYPES.ANY_REWARDS:
        case PRODUCT_TYPES.STORE_CREDIT:
            iconKey = 'diamond';
            // eslint-disable-next-line jsx-control-statements/jsx-use-if-tag
            message = focusVariant === FOCUS_VARIANTS.subsequentYearRewards
                ? (
                    <FormattedMessage
                        id="edb.credit-card.highlights.subsequent-year-reward"
                        defaultMessage="Subsequent year reward"
                    />
                )
                : (
                    <FormattedMessage
                        id="edb.credit-card.highlights.first-year-reward"
                        defaultMessage="First year reward"
                    />
                );

            break;

        case PRODUCT_TYPES.BALANCE_TRANSFER:
            iconKey = 'diamond';
            message = (
                <FormattedMessage
                    id="edb.credit-card.highlights.balance-transfer-offer"
                    defaultMessage="Balance transfer offer"
                />
            );
            break;

        case PRODUCT_TYPES.LOW_INTEREST:
        case PRODUCT_TYPES.SECURED:
            break;      // no crown

        default:
            noticeError(new RangeError(`[FocusCrown] unsupported credit card productType: “${productType}”.`));
            break;
    }


    return (
        <If condition={iconKey}>
            <span className={`focus-crown
                rh-display-flex rh-align-items-center rh-text-xs weight-medium
                rh-pr-0_75 rh-pl-0_5 leading-s rh-position-absolute rh-bg-coconut`}
            >
                <IconChooser
                    className="crown-icon rh-display-inline-block rh-mr-0_25"
                    iconKey={iconKey}
                    size="XS"
                    strokeWidth="3"
                    stroke={Colours.YUZU_DARKEST}
                    includeOutline={false}
                />
                {message}
            </span>
        </If>
    );
}

FocusCrown.propTypes = {
    productType: PropTypes.string.isRequired,
    focusVariant: PropTypes.oneOf(Object.values(FOCUS_VARIANTS)),
};


export default styled(observer(CardLayoutHighlights))`
    box-shadow: 0 0 1px ${Colours.BLACKBERRY_LIGHT};

    > .focus-area,          // smaller, left, Yuzu background area with big number (for everything else)
    > .details-area {       // for CIBC + BT
        flex: 1 0 16rem;

        > .focus-crown {
            left: 10px;
            top: 0;
            transform: translateY(-50%);
            border: 1px solid ${Colours.YUZU_DARK};
            border-radius: ${Sizes.SPACING.ONE_AND_A_QUARTER};

            > .crown-icon {
                flex-shrink: 0;
            }
        }
    }

    // larger, right, white background area with text details
    > .details-area {
        flex: 150 1 340px;  // very greedy when extra width available
    }
`;
